import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faChevronDown,
  faTimes,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  faUser as farUser
} from '@fortawesome/free-regular-svg-icons';
import {
  CircleAlert,
  LayoutDashboard,
  LoaderCircle,
  RefreshCw
} from 'lucide-react';
import { DashboardViewModel } from '../viewmodels/DashboardViewModel';
import { DashboardCharts } from './DashboardCharts';
import { DashboardStats } from './DashboardStats';
import { Requests } from './Requests';
import { RequestDetails } from './RequestDetails';
import { Payments } from './Payments';
import { PaymentPlans } from './PaymentPlans';
import { RegisteredMechanics } from './RegisteredMechanics';
import { PendingRegistrations } from './PendingRegistrations';
import { PaymentDueServiceProviders } from './PaymentDueServiceProviders';
import { RegisteredServiceUsers } from './RegisteredServiceUsers';
import { UserManagement } from './UserManagement';
import { Settings } from './Settings';
import { ServiceProviderDetailView } from './ServiceProviderDetailView';
import { Notifications } from './Notifications';
import { Promotions } from './Promotions';
import { ServiceProvider, serviceProviderService } from '../services/serviceProviderService';

// Initialize ViewModel outside component to prevent recreation
const dashboardViewModel = new DashboardViewModel();

// Use ServiceProvider interface from the service
type Mechanic = ServiceProvider;

const MenuIcon = ({ src }: { src: string }) => (
  <span
    aria-hidden="true"
    className="block w-5 h-5 bg-current"
    style={{
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }}
  />
);

const formatDashboardGeneratedAt = (value?: string) => {
  if (!value) return null;

  const generatedAt = new Date(value);
  if (Number.isNaN(generatedAt.getTime())) return null;

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(generatedAt);
};

export const Dashboard: React.FC = observer(() => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [selectedMechanic, setSelectedMechanic] = useState<Mechanic | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync activeMenu with URL path on mount and URL changes
  // Sync activeMenu with URL path on mount and URL changes
  useEffect(() => {
    const path = location.pathname;
    // Extract menu key from path (remove leading slash)
    // Handle sub-routes like /requests/123 by taking only the first segment
    const pathSegments = path.substring(1).split('/');
    const menuKey = pathSegments[0] || 'dashboard';

    // Map of valid menu keys
    const validMenuKeys = [
      'dashboard',
      'requests',
      'payments',
      'payment-plans',
      'registered-mechanics',
      'payment-due',
      'pending-registrations',
      'registered-drivers',
      'notifications',
      'promotions',
      'user-management',
      'settings'
    ];

    if (validMenuKeys.includes(menuKey)) {
      setActiveMenu(menuKey);
    } else {
      // If path doesn't match any menu, default to dashboard
      // Only redirect if it's not a known valid path pattern
      setActiveMenu('dashboard');
      if (path !== '/dashboard') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    dashboardViewModel.initialize();

    // Keep privileged session data scoped to this browser tab.
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
        // User data logging removed for security
      } catch (error) {
        console.error('❌ Error parsing user data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('loginTimestamp');
    // Logout logging removed for security
    navigate('/');
  };

  const toggleMenu = (menuKey: string) => {
    // Reset view mode when switching menus
    if (viewMode === 'detail') {
      setViewMode('list');
      setSelectedMechanic(null);
    }
    setExpandedMenus(prev =>
      prev.includes(menuKey)
        ? prev.filter(item => item !== menuKey)
        : [...prev, menuKey]
    );
  };

  const handleViewMechanic = (mechanic: Mechanic) => {
    setSelectedMechanic(mechanic);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedMechanic(null);
  };

  const handleRefreshMechanic = async () => {
    if (selectedMechanic?.contactNumber) {
      try {
        // Service provider refresh logging removed for security
        const updatedMechanic = await serviceProviderService.getServiceProviderByContactNumber(selectedMechanic.contactNumber);
        if (updatedMechanic) {
          // Service provider data refresh logging removed for security
          setSelectedMechanic(updatedMechanic);
        } else {
          console.warn('⚠️ Could not refresh service provider data');
        }
      } catch (error) {
        console.error('❌ Error refreshing service provider data:', error);
      }
    }
  };

  const menuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: '/menu-icons/dashboard.png',
      hasSubmenu: false
    },
    {
      key: 'requests',
      label: 'Requests',
      icon: '/menu-icons/requests.png',
      hasSubmenu: false
    },
    {
      key: 'payments',
      label: 'Payments',
      icon: '/menu-icons/payments.png',
      hasSubmenu: false
    },
    {
      key: 'mechanics',
      label: 'Service Providers',
      icon: '/menu-icons/service-providers.png',
      hasSubmenu: true,
      submenu: [
        { key: 'registered-mechanics', label: 'Registered' },
        { key: 'payment-due', label: 'Payment Due' },
        { key: 'pending-registrations', label: 'Pending Registrations' }
      ]
    },
    {
      key: 'drivers',
      label: 'Service Users',
      icon: '/menu-icons/service-users.png',
      hasSubmenu: true,
      submenu: [
        { key: 'registered-drivers', label: 'All Service Users' }
        // { key: 'blocked-drivers', label: 'Blocked' }
      ]
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: '/menu-icons/notifications.png',
      hasSubmenu: false
    },
    {
      key: 'promotions',
      label: 'Promotions',
      icon: '/menu-icons/promotions.png',
      hasSubmenu: false
    },
    {
      key: 'user-management',
      label: 'User Management',
      icon: '/menu-icons/user-management.png',
      hasSubmenu: false,
      requiresAdmin: true
    },
    {
      key: 'payment-plans',
      label: 'Plan Management',
      icon: '/menu-icons/plan-management.png',
      hasSubmenu: false
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: '/menu-icons/settings.png',
      hasSubmenu: false
    }
  ];
  const visibleMenuItems = menuItems.filter(item => !item.requiresAdmin || user?.role === 'admin');

  const dashboardGeneratedAt = formatDashboardGeneratedAt(dashboardViewModel.kpiData?.overview.generatedAt);

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {/* Sidebar */}
      <div className={`bg-white border-r border-slate-200 transition-[width] duration-300 ease-in-out flex-shrink-0 ${isSidebarOpen ? 'w-64' : 'w-16'
        } hidden md:block`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
          {isSidebarOpen && (
            <div className="flex items-center justify-center w-full">
              <img
                src="/uniprime-new-logo-trans.png"
                alt="Uniprime Logo"
                className="h-10 w-40 object-contain"
              />
            </div>
          )}
          {!isSidebarOpen && (
            <div className="flex items-center justify-center w-full">
              <div className="h-8 w-8 overflow-hidden" aria-label="Uniprime">
                <img
                  src="/uniprime-new-logo-trans.png"
                  alt=""
                  className="h-8 w-auto max-w-none object-contain object-left"
                />
              </div>
            </div>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <FontAwesomeIcon icon={faBars} className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Sidebar Menu */}
        <nav className="mt-4 px-2 overflow-y-auto h-[calc(100vh-4rem)]">
          <ul className="space-y-0.5">
            {visibleMenuItems.map((item) => (
              <li key={item.key}>
                {item.hasSubmenu ? (
                  <div>
                    <button
                      onClick={() => toggleMenu(item.key)}
                      className={`relative w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                    >
                      {activeMenu === item.key && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-blue-600" />
                      )}
                      <span className="flex-shrink-0"><MenuIcon src={item.icon} /></span>
                      {isSidebarOpen && (
                        <>
                          <span className="ml-3 flex-1 text-left">{item.label}</span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`w-4 h-4 transition-transform ${expandedMenus.includes(item.key) ? 'rotate-180' : ''
                              }`}
                          />
                        </>
                      )}
                    </button>
                    {isSidebarOpen && expandedMenus.includes(item.key) && (
                      <ul className="ml-6 mt-1 space-y-0.5">
                        {item.submenu?.map((subItem) => (
                          <li key={subItem.key}>
                            <button
                              onClick={() => {
                                // Reset view mode when switching menus
                                if (viewMode === 'detail') {
                                  setViewMode('list');
                                  setSelectedMechanic(null);
                                }
                                navigate(`/${subItem.key}`);
                              }}
                              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${activeMenu === subItem.key
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                                }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${activeMenu === subItem.key ? 'bg-blue-600' : 'bg-slate-300'}`}></span>
                              <span className="ml-3">{subItem.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      // Reset view mode when switching menus
                      if (viewMode === 'detail') {
                        setViewMode('list');
                        setSelectedMechanic(null);
                      }
                      navigate(`/${item.key}`);
                    }}
                    className={`relative w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.key
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                  >
                    {activeMenu === item.key && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-blue-600" />
                    )}
                    <span className="flex-shrink-0"><MenuIcon src={item.icon} /></span>
                    {isSidebarOpen && (
                      <span className="ml-3">{item.label}</span>
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <nav className="bg-white border-b border-slate-200 flex-shrink-0 h-16">
          <div className="flex justify-between items-center h-full px-6">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <FontAwesomeIcon icon={faBars} className="w-5 h-5 text-slate-500" />
              </button>

              <h2 className="text-base font-medium text-slate-600">
                Welcome back, <span className="text-slate-900 font-semibold">{user?.fullName || 'Admin'}</span>
              </h2>
            </div>

            {/* Right side - User Menu */}
            <div className="flex items-center space-x-4">
              {/* User Menu */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center space-x-2 p-1.5 pr-3 rounded-lg hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={farUser} className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium text-sm">{user?.fullName || 'Admin'}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* User Dropdown */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center space-x-2"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/40"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close navigation menu"
            />
            <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl">
              {/* Mobile Sidebar Header */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
                <div>
                  <h1 className="text-xl font-bold text-slate-900">UniPrime</h1>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Mobile Sidebar Menu */}
              <nav className="mt-4 px-2 overflow-y-auto h-[calc(100vh-4rem)]">
                <ul className="space-y-0.5">
                  {visibleMenuItems.map((item) => (
                    <li key={item.key}>
                      {item.hasSubmenu ? (
                        <div>
                          <button
                            onClick={() => toggleMenu(item.key)}
                            className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.key
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                              }`}
                          >
                            <span className="flex-shrink-0"><MenuIcon src={item.icon} /></span>
                            <span className="ml-3 flex-1 text-left">{item.label}</span>
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`w-4 h-4 transition-transform ${expandedMenus.includes(item.key) ? 'rotate-180' : ''
                                }`}
                            />
                          </button>
                          {expandedMenus.includes(item.key) && (
                            <ul className="ml-6 mt-1 space-y-0.5">
                              {item.submenu?.map((subItem) => (
                                <li key={subItem.key}>
                                  <button
                                    onClick={() => {
                                      // Reset view mode when switching menus
                                      if (viewMode === 'detail') {
                                        setViewMode('list');
                                        setSelectedMechanic(null);
                                      }
                                      navigate(`/${subItem.key}`);
                                      setIsSidebarOpen(false);
                                    }}
                                    className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${activeMenu === subItem.key
                                      ? 'bg-blue-50 text-blue-700 font-medium'
                                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                                      }`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${activeMenu === subItem.key ? 'bg-blue-600' : 'bg-slate-300'}`}></span>
                                    <span className="ml-3">{subItem.label}</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            // Reset view mode when switching menus
                            if (viewMode === 'detail') {
                              setViewMode('list');
                              setSelectedMechanic(null);
                            }
                            navigate(`/${item.key}`);
                            setIsSidebarOpen(false);
                          }}
                          className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.key
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                        >
                          <span className="flex-shrink-0"><MenuIcon src={item.icon} /></span>
                          <span className="ml-3">{item.label}</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Page Content - Scrollable */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="mx-auto max-w-screen-2xl p-4 sm:p-6">
            {/* Dashboard Content */}
            {activeMenu === 'dashboard' && (
              <div className="space-y-6">
                <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6" aria-labelledby="dashboard-title">
                  <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
                  <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
                  <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
                        <LayoutDashboard className="h-3.5 w-3.5" aria-hidden="true" />
                        Command center
                      </div>
                      <h1 id="dashboard-title" className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Dashboard</h1>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                        Monitor platform activity, audience health, revenue performance, and operational trends from one workspace.
                      </p>
                      {dashboardGeneratedAt && (
                        <p className="mt-3 text-xs font-medium text-slate-400">Data generated {dashboardGeneratedAt}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => dashboardViewModel.fetchDashboardStats()}
                      disabled={dashboardViewModel.isLoading}
                      className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${dashboardViewModel.isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                      {dashboardViewModel.isLoading ? 'Refreshing…' : 'Refresh data'}
                    </button>
                  </div>
                </section>

                {dashboardViewModel.isLoading && !dashboardViewModel.kpiData && (
                  <section className="rounded-xl border border-slate-200 bg-white px-5 py-14 text-center shadow-sm" role="status" aria-live="polite">
                    <LoaderCircle className="mx-auto h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
                    <h2 className="mt-4 text-sm font-semibold text-slate-900">Loading dashboard insights…</h2>
                    <p className="mt-1 text-sm text-slate-500">Fetching the latest platform metrics.</p>
                  </section>
                )}

                {dashboardViewModel.error && (
                  <section className="flex flex-col gap-4 rounded-xl border border-red-200 bg-red-50 p-5 sm:flex-row sm:items-center sm:justify-between" role="alert">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-red-600 shadow-sm">
                        <CircleAlert className="h-[18px] w-[18px]" aria-hidden="true" />
                      </span>
                      <div>
                        <h2 className="text-sm font-semibold text-red-950">Unable to load dashboard data</h2>
                        <p className="mt-1 text-sm text-red-700">{dashboardViewModel.error}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => dashboardViewModel.fetchDashboardStats()}
                      disabled={dashboardViewModel.isLoading}
                      className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg bg-red-700 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Try again
                    </button>
                  </section>
                )}

                {dashboardViewModel.kpiData && (
                  <>
                    <DashboardStats kpiData={dashboardViewModel.kpiData} formatCurrency={dashboardViewModel.formatCurrency} />
                    <DashboardCharts kpiData={dashboardViewModel.kpiData} formatCurrency={dashboardViewModel.formatCurrency} />
                  </>
                )}
              </div>
            )}

            {/* Other Menu Content */}
            {activeMenu !== 'dashboard' && (
              <>
                {/* Requests Section */}
                {activeMenu === 'requests' && (
                  location.pathname.split('/').length > 2 ? <RequestDetails /> : <Requests />
                )}

                {/* Payments Section */}
                {activeMenu === 'payments' && (
                  <Payments />
                )}

                {/* Payment Plans Section */}
                {activeMenu === 'payment-plans' && (
                  <PaymentPlans />
                )}

                {/* Registered Mechanics Section */}
                {activeMenu === 'registered-mechanics' && (
                  <>
                    {viewMode === 'list' ? (
                      <RegisteredMechanics onViewMechanic={handleViewMechanic} />
                    ) : (
                      selectedMechanic && (
                        <ServiceProviderDetailView
                          mechanic={selectedMechanic}
                          onBack={handleBackToList}
                          onRefresh={handleRefreshMechanic}
                        />
                      )
                    )}
                  </>
                )}

                {/* Payment Due Service Providers Section */}
                {activeMenu === 'payment-due' && (
                  <>
                    {viewMode === 'list' ? (
                      <PaymentDueServiceProviders onViewMechanic={handleViewMechanic} />
                    ) : (
                      selectedMechanic && (
                        <ServiceProviderDetailView
                          mechanic={selectedMechanic}
                          onBack={handleBackToList}
                          onRefresh={handleRefreshMechanic}
                        />
                      )
                    )}
                  </>
                )}

                {/* Pending Registrations Section */}
                {activeMenu === 'pending-registrations' && (
                  <PendingRegistrations />
                )}

                {/* Registered Service Users Section */}
                {activeMenu === 'registered-drivers' && (
                  <RegisteredServiceUsers />
                )}

                {/* User Management Section */}
                {activeMenu === 'user-management' && user?.role === 'admin' && (
                  <UserManagement />
                )}

                {/* Notifications Section */}
                {activeMenu === 'notifications' && (
                  <Notifications />
                )}

                {/* Promotions Section */}
                {activeMenu === 'promotions' && (
                  <Promotions />
                )}

                {/* Settings Section */}
                {activeMenu === 'settings' && (
                  <Settings />
                )}

                {/* Other Menu Items - Placeholder Content */}
                {!['requests', 'payments', 'payment-plans', 'registered-mechanics', 'payment-due', 'pending-registrations', 'registered-drivers', 'notifications', 'promotions', 'user-management', 'settings'].includes(activeMenu) && (
                  <>
                    <div className="mb-6">
                      <h1 className="text-2xl font-semibold text-slate-900 capitalize">
                        {activeMenu.replace('-', ' ')}
                      </h1>
                      <p className="text-slate-500 mt-1">
                        Welcome to the {activeMenu.replace('-', ' ')} section
                      </p>
                    </div>

                    {/* Placeholder Content */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-slate-900">No content yet</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          This section will be implemented when you're ready to add specific functionality.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
});
